import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { FiX, FiChevronDown } from 'react-icons/fi'
import SkuInput from '@/components/contracts/SkuInput'
import ShippingPeriodInput from '@/components/contracts/ShippingPeriodInput'
import PaymentTermsInput from '@/components/contracts/PaymentTermsInput'
import ContractPriceInput from '@/components/contracts/ContractPriceInput'
import ProductSelectInput from '@/components/products/ProductSelectInput'
import CustomerSelectInput from '@/components/customers/CustomerSelectInput'
import { DEFAULT_CONTRACT_CURRENCY } from '@/constants/contractCurrencies'
import { getContractById, updateContract } from '@/services/api/contracts'
import { normalizeSkuForSave, parsePriceForInput, sanitizePriceForSave } from '@/utils/skuFormat'
import { formatShippingPeriodForSave, parseISODate, toISODateString } from '@/utils/shippingPeriod'
import { getVendors } from '@/services/api/vendors'
import { vendorsForContractPicker } from '@/utils/vendorFilters'
import type { Contract } from '@/services/api/contracts'
import type { Vendor } from '@/services/api/vendors'

interface EditContractModalProps {
  isOpen: boolean
  onClose: () => void
  contractId: string | null
  onSuccess?: () => void
}

const EditContractModal = ({ isOpen, onClose, contractId, onSuccess }: EditContractModalProps) => {
  const [contract, setContract] = useState<Contract | null>(null)
  const [vendors, setVendors] = useState<Vendor[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    title: '',
    contractNumber: '',
    vendorId: '',
    type: 'buy' as 'buy' | 'sale',
    productSpecifications: '',
    sku: '',
    quantity: '',
    price: '',
    priceCurrency: DEFAULT_CONTRACT_CURRENCY,
    incoterm: '',
    pol: '',
    pod: '',
    deliveryConditions: '',
    paymentTerms: '',
    customer: '',
  })

  const [shippingStartDate, setShippingStartDate] = useState<Date | null>(null)
  const [shippingEndDate, setShippingEndDate] = useState<Date | null>(null)
  const isContractNumberValid = (value: string) => /[A-Za-z0-9]/.test(value)

  useEffect(() => {
    if (!isOpen || !contractId) {
      setContract(null)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    Promise.all([getContractById(contractId), getVendors({ limit: 200 })])
      .then(([c, vRes]) => {
        setContract(c)
        setVendors(vendorsForContractPicker(vRes.vendors))
        setFormData({
          title: c.title,
          contractNumber: c.contractNumber,
          vendorId: c.vendorId,
          type: c.type,
          productSpecifications: c.productSpecifications ?? '',
          sku: c.sku ?? '',
          quantity: c.quantity ?? '',
          price: parsePriceForInput(c.price),
          priceCurrency: c.priceCurrency ?? DEFAULT_CONTRACT_CURRENCY,
          incoterm: c.incoterm ?? '',
          pol: c.pol ?? '',
          pod: c.pod ?? '',
          deliveryConditions: c.deliveryConditions ?? '',
          paymentTerms: c.paymentTerms ?? '',
          customer: c.customer ?? '',
        })
        setShippingStartDate(parseISODate(c.startDate))
        setShippingEndDate(parseISODate(c.endDate))
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load contract'))
      .finally(() => setLoading(false))
  }, [isOpen, contractId])

  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.width = '100%'
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.position = ''
        document.body.style.top = ''
        document.body.style.width = ''
        document.body.style.overflow = ''
        window.scrollTo(0, scrollY)
      }
    }
  }, [isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!contractId) return
    setError(null)
    if (!formData.contractNumber.trim()) {
      setError('Contract number is required.')
      return
    }
    if (!isContractNumberValid(formData.contractNumber.trim())) {
      setError('Contract number must include at least one letter or number.')
      return
    }
    setSaving(true)
    try {
      await updateContract(contractId, {
        title: formData.contractNumber.trim(),
        contractNumber: formData.contractNumber.trim(),
        vendorId: formData.vendorId || undefined,
        type: formData.type,
        productSpecifications: formData.productSpecifications.trim() || undefined,
        sku: normalizeSkuForSave(formData.sku),
        quantity: formData.quantity.trim() || undefined,
        price: sanitizePriceForSave(formData.price),
        priceCurrency: formData.priceCurrency || DEFAULT_CONTRACT_CURRENCY,
        incoterm: formData.incoterm.trim() || undefined,
        pol: formData.pol.trim() || undefined,
        pod: formData.pod.trim() || undefined,
        deliveryConditions: formData.deliveryConditions.trim() || undefined,
        startDate: toISODateString(shippingStartDate),
        endDate: toISODateString(shippingEndDate),
        deliveryPeriod: formatShippingPeriodForSave(shippingStartDate, shippingEndDate),
        paymentTerms: formData.paymentTerms.trim() || undefined,
        customer: formData.customer.trim() || undefined,
      })
      onSuccess?.()
      onClose()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update contract')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end"
      onClick={onClose}
      style={{ zIndex: 60, overflow: 'hidden' }}
    >
      <div
        className="bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          fontFamily: 'Lexend Deca',
          width: '600px',
          maxWidth: '600px',
          height: '100vh',
          zIndex: 70,
          borderTopLeftRadius: '12px',
          borderBottomLeftRadius: '12px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          className="flex items-center justify-between border-b border-gray-200 flex-shrink-0"
          style={{
            height: '74px',
            backgroundColor: '#F2F4F2',
            borderTopLeftRadius: '12px',
            paddingLeft: '24px',
            paddingRight: '24px',
          }}
        >
          <h2
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontSize: '18px',
              lineHeight: '26px',
              color: '#212126',
            }}
          >
            Edit Contract
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center hover:opacity-80"
            style={{ width: 24, height: 24 }}
          >
            <FiX style={{ width: 24, height: 24, color: '#212126' }} />
          </button>
        </div>

        <form
          id="edit-contract-form"
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto flex flex-col"
          style={{ padding: 24, gap: 20 }}
        >
          {error && (
            <div style={{ fontFamily: 'Lexend Deca', fontSize: 14, color: '#E93323' }}>
              {error}
            </div>
          )}

          {loading ? (
            <p style={{ fontFamily: 'Lexend Deca', fontSize: 14, color: '#69697A' }}>Loading...</p>
          ) : contract ? (
            <div className="flex flex-col gap-4">
              {/* Row 1: Contract No. | Contract Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: 14, lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: 8 }}>Contract No. *</label>
                  <input
                    type="text"
                    value={formData.contractNumber}
                    onChange={(e) => {
                      const contractNumber = e.target.value
                      setFormData({ ...formData, contractNumber, title: contractNumber })
                    }}
                    required className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={{ fontFamily: 'Lexend Deca', fontSize: 12, height: 40, border: '1px solid #E9E9EA', borderRadius: 8 }} />
                </div>
                <div>
                  <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: 14, lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: 8 }}>Contract Type *</label>
                  <div className="flex gap-4" style={{ height: 40, alignItems: 'center' }}>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="editType" checked={formData.type === 'buy'} onChange={() => setFormData({ ...formData, type: 'buy' })} style={{ width: 16, height: 16, accentColor: '#212126' }} />
                      <span style={{ fontFamily: 'Lexend Deca', fontSize: 14, color: '#212126' }}>Buy</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="editType" checked={formData.type === 'sale'} onChange={() => setFormData({ ...formData, type: 'sale' })} style={{ width: 16, height: 16, accentColor: '#212126' }} />
                      <span style={{ fontFamily: 'Lexend Deca', fontSize: 14, color: '#212126' }}>Sale</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Row 2: Supplier (Buy) OR Supplier | Customer (Sale) */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: 14, lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: 8 }}>Supplier *</label>
                  <div className="relative">
                    <select value={formData.vendorId} onChange={(e) => setFormData({ ...formData, vendorId: e.target.value })} required className="w-full px-4 py-2 pr-8 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none" style={{ fontFamily: 'Lexend Deca', fontSize: 12, height: 40, border: '1px solid #E9E9EA', borderRadius: 8 }}>
                      <option value="">Select supplier</option>
                      {vendors.map((v) => (<option key={v.id} value={v.id}>{v.name}</option>))}
                    </select>
                    <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ width: 16, height: 16, color: '#212126' }} />
                  </div>
                </div>
                {formData.type === 'sale' && (
                  <div>
                    <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: 14, lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: 8 }}>Customer</label>
                    <CustomerSelectInput
                      value={formData.customer}
                      onChange={(customer) => setFormData((prev) => ({ ...prev, customer }))}
                    />
                  </div>
                )}
              </div>

              {/* Product | SKU */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: 14, lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: 8 }}>Product</label>
                  <ProductSelectInput
                    value={formData.productSpecifications}
                    onChange={(productSpecifications) =>
                      setFormData((prev) => ({ ...prev, productSpecifications }))
                    }
                  />
                </div>
                <div>
                  <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: 14, lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: 8 }}>SKU (kg)</label>
                  <SkuInput
                    value={formData.sku}
                    onChange={(sku) => setFormData((prev) => ({ ...prev, sku }))}
                  />
                </div>
              </div>

              {/* Qty */}
              {formData.type === 'sale' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: 14, lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: 8 }}>Qty</label>
                    <input type="text" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: e.target.value })} placeholder="Enter qty" className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={{ fontFamily: 'Lexend Deca', fontSize: 12, height: 40, border: '1px solid #E9E9EA', borderRadius: 8 }} />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: 14, lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: 8 }}>Qty</label>
                    <input type="text" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: e.target.value })} placeholder="Enter qty" className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={{ fontFamily: 'Lexend Deca', fontSize: 12, height: 40, border: '1px solid #E9E9EA', borderRadius: 8 }} />
                  </div>
                </div>
              )}

              <ContractPriceInput
                price={formData.price}
                currency={formData.priceCurrency}
                onPriceChange={(price) => setFormData((prev) => ({ ...prev, price }))}
                onCurrencyChange={(priceCurrency) => setFormData((prev) => ({ ...prev, priceCurrency }))}
              />

              {/* Incoterm (Sale) OR Incoterm | POL (Buy) */}
              {formData.type === 'sale' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: 14, lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: 8 }}>Incoterm</label>
                    <input type="text" value={formData.incoterm} onChange={(e) => setFormData({ ...formData, incoterm: e.target.value })} placeholder="Enter incoterm" className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={{ fontFamily: 'Lexend Deca', fontSize: 12, height: 40, border: '1px solid #E9E9EA', borderRadius: 8 }} />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: 14, lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: 8 }}>Incoterm</label>
                    <input type="text" value={formData.incoterm} onChange={(e) => setFormData({ ...formData, incoterm: e.target.value })} placeholder="Enter incoterm" className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={{ fontFamily: 'Lexend Deca', fontSize: 12, height: 40, border: '1px solid #E9E9EA', borderRadius: 8 }} />
                  </div>
                  <div>
                    <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: 14, lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: 8 }}>POL</label>
                    <input type="text" value={formData.pol} onChange={(e) => setFormData({ ...formData, pol: e.target.value })} placeholder="Enter port of loading" className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={{ fontFamily: 'Lexend Deca', fontSize: 12, height: 40, border: '1px solid #E9E9EA', borderRadius: 8 }} />
                  </div>
                </div>
              )}

              {/* POL | POD (Sale) OR POD | Delivery Conditions (Buy) */}
              {formData.type === 'sale' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: 14, lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: 8 }}>POL</label>
                    <input type="text" value={formData.pol} onChange={(e) => setFormData({ ...formData, pol: e.target.value })} placeholder="Enter port of loading" className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={{ fontFamily: 'Lexend Deca', fontSize: 12, height: 40, border: '1px solid #E9E9EA', borderRadius: 8 }} />
                  </div>
                  <div>
                    <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: 14, lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: 8 }}>POD</label>
                    <input type="text" value={formData.pod} onChange={(e) => setFormData({ ...formData, pod: e.target.value })} placeholder="Enter port of discharge" className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={{ fontFamily: 'Lexend Deca', fontSize: 12, height: 40, border: '1px solid #E9E9EA', borderRadius: 8 }} />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: 14, lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: 8 }}>POD</label>
                    <input type="text" value={formData.pod} onChange={(e) => setFormData({ ...formData, pod: e.target.value })} placeholder="Enter port of discharge" className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={{ fontFamily: 'Lexend Deca', fontSize: 12, height: 40, border: '1px solid #E9E9EA', borderRadius: 8 }} />
                  </div>
                  <div>
                    <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: 14, lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: 8 }}>Delivery Conditions</label>
                    <input type="text" value={formData.deliveryConditions} onChange={(e) => setFormData({ ...formData, deliveryConditions: e.target.value })} placeholder="Enter delivery conditions" className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={{ fontFamily: 'Lexend Deca', fontSize: 12, height: 40, border: '1px solid #E9E9EA', borderRadius: 8 }} />
                  </div>
                </div>
              )}

              {/* Delivery Conditions | Shipping Period (Sale) OR Shipping Period | Payment Terms (Buy) */}
              {formData.type === 'sale' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: 14, lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: 8 }}>Delivery Conditions</label>
                    <input type="text" value={formData.deliveryConditions} onChange={(e) => setFormData({ ...formData, deliveryConditions: e.target.value })} placeholder="Enter delivery conditions" className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={{ fontFamily: 'Lexend Deca', fontSize: 12, height: 40, border: '1px solid #E9E9EA', borderRadius: 8 }} />
                  </div>
                  <div>
                    <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: 14, lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: 8 }}>Shipping Period</label>
                    <ShippingPeriodInput
                      startDate={shippingStartDate}
                      endDate={shippingEndDate}
                      onChange={(start, end) => {
                        setShippingStartDate(start)
                        setShippingEndDate(end)
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: 14, lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: 8 }}>Shipping Period</label>
                    <ShippingPeriodInput
                      startDate={shippingStartDate}
                      endDate={shippingEndDate}
                      onChange={(start, end) => {
                        setShippingStartDate(start)
                        setShippingEndDate(end)
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: 14, lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: 8 }}>Payment Terms</label>
                    <PaymentTermsInput
                      value={formData.paymentTerms}
                      onChange={(paymentTerms) => setFormData((prev) => ({ ...prev, paymentTerms }))}
                    />
                  </div>
                </div>
              )}

              {/* Payment Terms (Sale only) */}
              {formData.type === 'sale' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: 14, lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: 8 }}>Payment Terms</label>
                    <PaymentTermsInput
                      value={formData.paymentTerms}
                      onChange={(paymentTerms) => setFormData((prev) => ({ ...prev, paymentTerms }))}
                    />
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </form>

        {contract && (
          <div
            className="flex items-center justify-end gap-4 flex-shrink-0 border-t border-gray-200"
            style={{
              height: 74,
              backgroundColor: '#F2F4F2',
              borderBottomLeftRadius: 12,
              paddingLeft: 24,
              paddingRight: 24,
            }}
          >
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 500,
                fontSize: 14,
                color: '#212126',
                backgroundColor: '#FFFFFF',
                border: '1px solid #E9E9EA',
                borderRadius: 8,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="edit-contract-form"
              disabled={saving || loading}
              className="px-6 py-2 rounded-lg hover:opacity-90 disabled:opacity-60"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 500,
                fontSize: 14,
                color: '#FFFFFF',
                backgroundColor: '#212126',
                borderRadius: 8,
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default EditContractModal
